/**
 * คำศัพท์ — หน่วยที่ใช้ได้จริง
 *
 * คันจิเดี่ยวอ่านอนิเมะไม่ได้ · รู้ว่า 好 แปลว่าชอบ แต่เจอ 大好き ในเพลงก็ยังอ่านไม่ออก
 * เพราะไม่รู้ว่ามันประกอบเป็นคำยังไงและออกเสียงว่าอะไร · deck นี้เก็บทั้งสามอย่าง
 * ไว้ในใบเดียว — รูปที่เจอจริง คำอ่าน ความหมาย แล้วแตกดูคันจิข้างในได้ทีหลัง
 *
 * ลำดับมาจากการนับคำในคลังประโยค Tatoeba (ภาษาพูดจริง) ไม่ใช่คลังข่าว
 * ดู scripts/build-vocab.ts ว่าทำไมถึงต้องนับเอง
 */
import type { Card } from '@/core/types'
import type { Deck, Group } from './types'

interface VocabRow {
  w: string
  r: string
  g: string[]
  pos: string[]
  n: number
  s?: string
  th?: string
  st?: string
}

/** เขียนด้วย kana ล้วนไหม — พวกนี้เรียนได้ทันทีโดยไม่ต้องรู้คันจิ */
function isKana(s: string): boolean {
  return [...s].every((c) => (c >= '぀' && c <= 'ヿ') || c === 'ー')
}

/** คันจิที่อยู่ในคำนี้ — ใช้โยงไปดูชิ้นส่วนกับลำดับขีด */
function kanjiIn(s: string): string[] {
  return [...new Set([...s].filter((c) => c >= '一' && c <= '鿿'))]
}

const POS_TH: Record<string, string> = {
  n: 'คำนาม',
  pn: 'สรรพนาม',
  adv: 'คำวิเศษณ์',
  'adj-i': 'คำคุณศัพท์ い',
  'adj-na': 'คำคุณศัพท์ な',
  'adj-no': 'คำคุณศัพท์ の',
  'adj-pn': 'คำขยายนาม',
  vs: 'ทำเป็นกริยาด้วย する',
  v1: 'กริยากลุ่ม 2',
  v5u: 'กริยากลุ่ม 1',
  v5k: 'กริยากลุ่ม 1',
  v5s: 'กริยากลุ่ม 1',
  v5r: 'กริยากลุ่ม 1',
  v5m: 'กริยากลุ่ม 1',
  v5t: 'กริยากลุ่ม 1',
  v5n: 'กริยากลุ่ม 1',
  v5g: 'กริยากลุ่ม 1',
  v5b: 'กริยากลุ่ม 1',
  vk: 'กริยาพิเศษ',
  'vs-i': 'กริยาพิเศษ',
  conj: 'คำเชื่อม',
  num: 'จำนวนนับ',
}

const cards: Card[] = []
let loadPromise: Promise<void> | null = null

async function load(): Promise<void> {
  if (cards.length > 0) return

  const res = await fetch(`${import.meta.env.BASE_URL}data/vocab-core.json`)
  if (!res.ok) throw new Error(`โหลดคำศัพท์ไม่ได้ — รัน npm run data:vocab ก่อน (${res.status})`)
  const rows = (await res.json()) as VocabRow[]

  /*
   * คำที่เขียนด้วย kana ล้วนมาก่อนทั้งหมด เพราะอ่านออกตั้งแต่วันที่ kana คล่อง
   * ไม่ต้องรอเรียนคันจิสักตัว · ที่เหลือเรียงตามความถี่เดิม
   */
  const ordered = [...rows].sort((a, b) => {
    const ka = isKana(a.w) ? 0 : 1
    const kb = isKana(b.w) ? 0 : 1
    return ka - kb || b.n - a.n
  })

  ordered.forEach((v, i) => {
    const tags = [isKana(v.w) ? 'kana' : 'kanji']
    // คำที่ยังไม่ได้แปลไทยยังใช้ได้ แต่ไม่ควรโผล่มาก่อนคำที่แปลแล้ว
    if (!v.th) tags.push('rare')
    const pos = v.pos[0]
    if (pos && POS_TH[pos]) tags.push(`pos-${pos}`)

    cards.push({
      id: `vocab:${v.w}`,
      deck: 'vocab',
      tags,
      front: v.w,
      back: v.th || v.g[0] || '',
      alts: v.g,
      extra: {
        reading: v.r,
        meanings: v.g,
        pos: v.pos,
        posTh: pos ? (POS_TH[pos] ?? '') : '',
        /** ประโยคตัวอย่างจาก Tatoeba */
        sentence: v.s ?? '',
        /** คำแปลไทยของประโยค — เขียนเองใน data-th/sentences/ */
        sentenceTh: v.st ?? '',
        /** เจอกี่ครั้งในคลังประโยค */
        freq: v.n,
        /** คันจิที่ประกอบ — โยงไปดูชิ้นส่วน/ลำดับขีดได้ */
        parts: kanjiIn(v.w),
        order: i,
      },
    })
  })
}

const groups: Group[] = [
  { tag: 'kana', label: 'คำ kana ล้วน', section: 'รูปเขียน' },
  { tag: 'kanji', label: 'คำที่มีคันจิ', section: 'รูปเขียน' },
  { tag: 'pos-n', label: 'คำนาม', section: 'ชนิดคำ' },
  { tag: 'pos-adj-i', label: 'คุณศัพท์ い', section: 'ชนิดคำ' },
  { tag: 'pos-adj-na', label: 'คุณศัพท์ な', section: 'ชนิดคำ' },
  { tag: 'pos-adv', label: 'วิเศษณ์', section: 'ชนิดคำ' },
]

export const vocabDeck: Deck = {
  id: 'vocab',
  titleJa: '単語ドリル',
  titleTh: 'คำศัพท์ที่เจอบ่อยจริง — เรียงจากคลังประโยคพูด',
  modes: ['auto', 'lesson', 'recall', 'glyph-to-meaning', 'meaning-to-glyph'],
  scheduling: 'srs',
  cards,
  groups,
  ensureLoaded: () => {
    loadPromise ??= load()
    return loadPromise
  },
}
